<template>
  <div class="w-full flex flex-col flex-grow min-h-0">
  <!-- Sign in -->
    <AuthCard v-if="!onCodeStep" lockup title="Sign in to DLSTracker">
      <form class="flex flex-col gap-5" @submit.prevent="onSubmit" novalidate>
      <!-- Email -->
        <div class="flex flex-col gap-1.5">
          <label for="email" class="font-semibold">Email</label>
          <input id="email" name="email" type="email" autocomplete="username" inputmode="email" v-model.trim="email" @input="error.email = ''"
            :disabled="isLoading" class="w-full h-10 sm:h-9 text-[16px] sm:text-sm" :class="{ 'input-not-validated': error.email }"
            :aria-invalid="!!error.email || null" aria-describedby="email-error">
          <div v-if="error.email" id="email-error" class="text-red font-semibold">{{ error.email }}</div>
        </div>
      <!-- Password -->
        <div class="flex flex-col gap-1.5">
          <div class="flex flex-row items-baseline justify-between">
            <label for="password" class="font-semibold">Password</label>
            <router-link to="/forgotpassword" class="custom-link text-xs font-semibold">Forgot password?</router-link>
          </div>
          <PasswordField id="password" autocomplete="current-password" v-model.trim="password" :invalid="!!error.password" :disabled="isLoading"
            @update:modelValue="error.password = ''" />
          <div v-if="error.password" class="text-red font-semibold">{{ error.password }}</div>
        </div>
      <!-- Server message -->
        <div v-if="loginError" role="alert" class="flex flex-row items-start gap-2 rounded-md bg-red-500/10 px-3 py-2 text-red font-semibold whitespace-normal">
          <ExclamationCircleIcon class="w-4 h-4 flex-shrink-0 mt-px" />
          <div>{{ loginError }}</div>
        </div>
        <button type="submit" class="bn-solid-sky w-full justify-center h-10 sm:h-9 text-sm" :class="{ 'is-busy': isLoading }" :disabled="isLoading">
          <div>{{ isLoading ? 'Signing in…' : 'Sign in' }}</div>
        </button>
      </form>
    </AuthCard>
  <!-- Code step -->
    <CodeStep v-else :challenge="challenge" @update:challenge="challenge = $event" @signedIn="onCodeSignedIn" @back="backToSignIn" />
  </div>
</template>

<script>
import router from "../router/index";
import { ref, inject, onMounted, computed, nextTick, watch } from "vue";
import axios from "axios";
import { isEmailValid } from '@/shared'
import { ExclamationCircleIcon } from '@heroicons/vue/solid'
import { useRoute } from 'vue-router'
import AuthCard from '@/components/auth/AuthCard.vue'
import PasswordField from '@/components/auth/PasswordField.vue'
import CodeStep from '@/components/auth/CodeStep.vue'
import { completeSignIn } from '@/components/auth/completeSignIn'

// The code step is kept in this tab's sessionStorage, so a reload or a Back from another site returns to it rather
// than to the password, whose new sign-in would delete the code just sent. The server keeps the sign-in 15 minutes
// after its last code (LoginCode expiry index); an older copy is dropped. The token alone cannot sign in: it needs
// the emailed code.
const CHALLENGE_KEY = 'signInChallenge'
const CHALLENGE_SECONDS = 900
const saveChallenge = (challenge) => {
  try {
    if (challenge?._id && challenge?.verificationToken) { sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify({ ...challenge, savedAt: Date.now() })) }
    else { sessionStorage.removeItem(CHALLENGE_KEY) }
  } catch { /* storage blocked: the step simply isn't kept */ }
}
const readSavedChallenge = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CHALLENGE_KEY) || 'null')
    const elapsed = (Date.now() - Number(saved?.savedAt)) / 1000
    if (typeof saved?._id !== 'string' || typeof saved?.verificationToken !== 'string' || !(elapsed >= 0 && elapsed < CHALLENGE_SECONDS)) {
      sessionStorage.removeItem(CHALLENGE_KEY)
      return {}
    }
    const challenge = { ...saved }
    delete challenge.savedAt
    // The countdowns were relative to when the code was sent
    if (typeof challenge.codeExpiresIn === 'number') { challenge.codeExpiresIn = Math.max(0, Math.round(challenge.codeExpiresIn - elapsed)) }
    if (typeof challenge.resendAfter === 'number') { challenge.resendAfter = Math.max(0, Math.round(challenge.resendAfter - elapsed)) }
    return challenge
  } catch { return {} }
}

export default {
  name: "LoginDialog",
  components: { AuthCard, PasswordField, CodeStep, ExclamationCircleIcon },
  setup() {
    const global = inject("global");
    const { login, authenticated } = global;
    const route = useRoute()
    const redirectRoute = route.query?.redirect || null

  // Refs
    const challenge = ref(readSavedChallenge())
    const isLoading = ref(false);
    const loginError = ref(null);
    const email = ref(null);
    const password = ref(null);
    const error = ref({ email: "", password: "" });
    const reLogin = ref(false);

  // Computed
    const onCodeStep = computed(() => !!(challenge.value?._id && challenge.value?.verificationToken))

  // API
    // Sign in with the email and password; the server answers with a sign-in, or a code challenge.
    const submitLogin = async () => {
      loginError.value = ''
      isLoading.value = true;
      const values = { email: email.value, password: password.value };
      password.value = "";
      if (authenticated.value.loggedIn) {reLogin.value = true;}

      await axios
        .post("/api/v1/auth/login", values)
        .then((response) => {
          if(response.data?.type === 'MFA') {
            challenge.value = response.data
          } else if(typeof response.data === 'object' && response.data._id && response.data.token) {
            finishSignIn(response.data)
          } else {
            loginError.value = "Problem with login";
          }
        })
        .catch((err) => {
          loginError.value = err.response?.data?.error || err.message;
        })
        .finally(()=>{
          isLoading.value = false
        })
    };

  // Helpers
    const finishSignIn = (user) => {
      loginError.value = ''
      saveChallenge(null)
      completeSignIn(user, { login, router, reLogin: reLogin.value, redirectRoute })
    }

  // Watchers
    // A new sign-in, a resend (CodeStep hands the new token back) and Back all replace the challenge
    watch(challenge, saveChallenge)

  // Lifecycle
    onMounted(()=>{
      localStorage.getItem('lastLogin') ? email.value = localStorage.getItem('lastLogin') : null
    })

  // Handlers
    const onSubmit = async () => {
      error.value = { email: "", password: "" }
      if (!email.value) { error.value.email = 'Enter your email address.' }
      else if (!isEmailValid(email.value)) { error.value.email = 'Enter a valid email address.' }
      if (!password.value) { error.value.password = 'Enter your password.' }
      if (!error.value.email && !error.value.password) { submitLogin() }
    };

    const onCodeSignedIn = (user) => finishSignIn(user)

    // Back from the code step: the email stays filled in, the password is typed again.
    const backToSignIn = () => {
      challenge.value = {}
      nextTick(() => document.getElementById('password')?.focus())
    }

    return {
      email,
      password,
      error,
      isLoading,
      loginError,
      challenge,
      onCodeStep,
      onSubmit,
      onCodeSignedIn,
      backToSignIn
    };
  },
};
</script>
